import { Check, Plus, Tag } from 'lucide-react';
import { useState } from 'react';

import { LabelChip, LabelDot } from '@/components/tasks/LabelChip';
import { LABEL_COLORS } from '@/lib/labels';
import { Button } from '@/components/ui/button';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { useCreateLabel, useLabels, useSetTaskLabels } from '@/hooks/useLabels';
import type { Label, LabelColor } from '@/lib/api/types';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';

/**
 * Choose which of *your* labels are on a task.
 *
 * The whole set is sent on every change, which is what a list of checkboxes
 * naturally produces — and the server replaces only your own labels, leaving
 * anyone else's filing of the same task alone.
 */
export function LabelPicker({
  taskId,
  selected,
}: {
  taskId: string;
  selected: Label[];
}) {
  const t = useT();
  const { data: available } = useLabels();
  const setLabels = useSetTaskLabels(taskId);
  const createLabel = useCreateLabel();
  const [creating, setCreating] = useState('');
  const [color, setColor] = useState<LabelColor>('slate');

  const selectedIds = new Set(selected.map((label) => label.id));

  function toggle(labelId: string) {
    const next = new Set(selectedIds);
    if (next.has(labelId)) next.delete(labelId);
    else next.add(labelId);
    setLabels.mutate([...next]);
  }

  function createAndApply() {
    const name = creating.trim();
    if (!name) return;
    createLabel.mutate(
      { name, color },
      {
        onSuccess: (label) => {
          setCreating('');
          setLabels.mutate([...selectedIds, label.id]);
        },
      },
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {selected.map((label) => (
        <LabelChip key={label.id} label={label} />
      ))}

      <Menu>
        <MenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5 text-content-subtle">
            <Tag className="h-3.5 w-3.5" aria-hidden />
            {selected.length === 0 ? t('label.add') : null}
          </Button>
        </MenuTrigger>

        <MenuContent align="start" className="w-60">
          <MenuLabel>{t('label.yours')}</MenuLabel>

          {(available ?? []).length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-content-subtle">
              {t('label.noneYet')}
            </p>
          ) : (
            (available ?? []).map((label) => (
              <MenuItem
                key={label.id}
                // Keep the menu open: labels are usually applied in twos and
                // threes, and reopening for each one is needless friction.
                onSelect={(event) => {
                  event.preventDefault();
                  toggle(label.id);
                }}
              >
                <LabelDot color={label.color} />
                <span className="flex-1 truncate">{label.name}</span>
                {selectedIds.has(label.id) ? (
                  <Check className="h-3.5 w-3.5 text-accent" aria-hidden />
                ) : null}
              </MenuItem>
            ))
          )}

          <MenuSeparator />
          <div className="p-1.5">
            <div className="mb-1.5 flex gap-1">
              {LABEL_COLORS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setColor(option)}
                  aria-label={t('label.colorNamed', { color: option })}
                  className={cn(
                    'flex h-5 w-5 items-center justify-center rounded',
                    color === option ? 'ring-2 ring-accent ring-offset-1' : '',
                  )}
                >
                  <LabelDot color={option} className="h-3 w-3" />
                </button>
              ))}
            </div>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                createAndApply();
              }}
              className="flex gap-1"
            >
              <input
                value={creating}
                onChange={(event) => setCreating(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
                placeholder={t('label.new')}
                maxLength={40}
                className={cn(
                  'min-w-0 flex-1 rounded border border-border bg-surface px-2 py-1',
                  'text-xs text-content outline-none focus:border-accent',
                )}
              />
              <Button
                type="submit"
                variant="secondary"
                size="icon-sm"
                disabled={!creating.trim() || createLabel.isPending}
                aria-label={t('label.create')}
              >
                <Plus className="h-3 w-3" aria-hidden />
              </Button>
            </form>
          </div>
        </MenuContent>
      </Menu>
    </div>
  );
}
